/**
 * PDF Viewer Page - Embedded viewer with download protection
 * Now includes site branding (header/footer)
 * @file src/web/features/materials/pages/view/[id].tsx
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@voilajsx/uikit/button';
import { Card, CardContent, CardTitle, CardDescription } from '@voilajsx/uikit/card';
import { SEO } from '../../../../shared/components';
import { config } from '../../../auth/config';
import {
  ArrowLeft,
  FileText,
  Loader2,
  AlertCircle,
  Lock,
  Phone,
  ZoomIn,
  ZoomOut,
  RotateCw,
  BookOpen,
  GraduationCap
} from 'lucide-react';
import { Link } from 'react-router-dom';

interface MaterialData {
  id: number;
  title: string;
  slug?: string;
  subject: string;
  classSection: string;
  accessType: 'free' | 'restricted';
  mimeType: string;
  description?: string;
}

const MaterialViewerPage: React.FC = () => {
  const { id } = useParams<{ id: string }>(); // Can be slug or ID
  const navigate = useNavigate();

  const [material, setMaterial] = useState<MaterialData | null>(null);
  const [viewUrl, setViewUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isRestricted, setIsRestricted] = useState(false);
  const [zoom, setZoom] = useState(100);

  useEffect(() => {
    const fetchMaterialAndUrl = async () => {
      if (!id) return;

      try {
        // Optional auth - add token if available
        const token = localStorage.getItem('auth_token');
        const headers: HeadersInit = {
          'Content-Type': 'application/json'
        };
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }

        // Fetch material details using slug or ID
        const materialRes = await fetch(`${config.api.baseUrl}/api/materials/${id}`, {
          headers
        });

        if (!materialRes.ok) {
          throw new Error('Material not found');
        }

        const materialData = await materialRes.json();
        setMaterial(materialData.material);

        // Fetch view URL using slug or ID
        const viewRes = await fetch(`${config.api.baseUrl}/api/materials/${id}/view`, {
          headers
        });

        if (!viewRes.ok) {
          const errorData = await viewRes.json();
          // For login_required, redirect to contact page (for non-logged-in users on premium materials)
          if (errorData.loginRequired || errorData.error === 'login_required') {
            navigate('/contact');
            return;
          }
          // For restricted errors, show the restricted message
          if (errorData.contactRequired || errorData.error === 'restricted') {
            setIsRestricted(true);
            setLoading(false);
            return;
          }
          throw new Error(errorData.error || 'Failed to get view access');
        }

        const viewData = await viewRes.json();
        if (viewData.success && viewData.fileUrl) {
          setViewUrl(viewData.fileUrl);
        }

      } catch (err: any) {
        console.error('Error loading material:', err);
        setError(err.message || 'Failed to load material');
      } finally {
        setLoading(false);
      }
    };

    fetchMaterialAndUrl();
  }, [id, navigate]);

  // Prevent right-click context menu on the viewer
  useEffect(() => {
    const preventContextMenu = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('.pdf-viewer-container')) {
        e.preventDefault();
      }
    };

    document.addEventListener('contextmenu', preventContextMenu);
    return () => document.removeEventListener('contextmenu', preventContextMenu);
  }, []);

  // Prevent keyboard shortcuts for saving/printing
  useEffect(() => {
    const preventShortcuts = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === 's' || e.key === 'p')) {
        e.preventDefault();
      }
    };

    document.addEventListener('keydown', preventShortcuts);
    return () => document.removeEventListener('keydown', preventShortcuts);
  }, []);

  if (loading) {
    return (
      <div className="container mx-auto px-4 max-w-7xl py-8">
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <Loader2 className="w-12 h-12 animate-spin text-green-600 mx-auto mb-4" />
            <p className="text-gray-600">Loading document...</p>
          </div>
        </div>
      </div>
    );
  }

  if (isRestricted) {
    return (
      <>
        <SEO title="Restricted Material - Tuition Point" />
        <div className="container mx-auto px-4 max-w-4xl py-8">
          <Card className="border-t-4 border-t-amber-500">
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Lock className="w-8 h-8 text-amber-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Premium Material</h2>
              <p className="text-gray-600 mb-6">
                This material requires authorization. Please contact our counsellors to request access.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/contact">
                  <Button className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600">
                    <Phone className="mr-2 h-4 w-4" />
                    Contact Counsellor
                  </Button>
                </Link>
                <Button variant="outline" onClick={() => navigate('/materials')}>
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back to Materials
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 max-w-4xl py-8">
        <Card className="border-t-4 border-t-red-500">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">Error</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <Button variant="outline" onClick={() => navigate('/materials')}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Materials
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isPDF = material?.mimeType === 'application/pdf';

  return (
    <>
      <SEO title={`${material?.title || 'Document'} - Tuition Point`} />
      <div className="container mx-auto px-4 max-w-7xl py-8">
        <div className="space-y-6">
          {/* Header with back button */}
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              onClick={() => navigate('/materials')}
              className="border-gray-300"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Materials
            </Button>
          </div>

          {/* Material Info Card */}
          <Card className="border-t-4 border-t-green-600 shadow-md overflow-hidden p-0">
            <div className="bg-gradient-to-r from-green-100 to-emerald-100 px-6 py-4">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <BookOpen className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">{material?.title}</CardTitle>
                    <CardDescription className="flex items-center gap-4 mt-1">
                      <span className="flex items-center gap-1">
                        <FileText className="h-3 w-3" />
                        {material?.subject}
                      </span>
                      <span className="flex items-center gap-1">
                        <GraduationCap className="h-3 w-3" />
                        {material?.classSection}
                      </span>
                    </CardDescription>
                  </div>
                </div>

                {/* Zoom controls for PDF */}
                {isPDF && (
                  <div className="flex items-center gap-2 bg-white rounded-lg px-3 py-2 shadow-sm">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setZoom(Math.max(50, zoom - 25))}
                      className="h-8 w-8 p-0"
                    >
                      <ZoomOut className="h-4 w-4" />
                    </Button>
                    <span className="text-sm font-medium min-w-[50px] text-center">{zoom}%</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setZoom(Math.min(200, zoom + 25))}
                      className="h-8 w-8 p-0"
                    >
                      <ZoomIn className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setZoom(100)}
                      className="h-8 w-8 p-0"
                      title="Reset zoom"
                    >
                      <RotateCw className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {/* PDF Viewer */}
            <CardContent className="p-0">
              <div
                className="pdf-viewer-container bg-gray-100"
                style={{ userSelect: 'none' }}
              >
                {viewUrl && isPDF ? (
                  <div className="flex justify-center p-4">
                    <iframe
                      src={`${viewUrl}#toolbar=0&navpanes=0&scrollbar=1&view=FitH`}
                      className="bg-white shadow-lg rounded-lg"
                      style={{
                        width: `${zoom}%`,
                        maxWidth: '1000px',
                        height: '80vh',
                        border: 'none'
                      }}
                      title={material?.title}
                    />
                  </div>
                ) : viewUrl ? (
                  <div className="flex items-center justify-center p-8">
                    <div className="bg-white rounded-lg shadow-lg p-8 text-center max-w-md">
                      <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">
                        Document Preview Not Available
                      </h3>
                      <p className="text-gray-600">
                        This file type cannot be previewed in the browser.
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-center p-8">
                    <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
                  </div>
                )}
              </div>

              {/* Footer Notice */}
              <div className="bg-gray-50 border-t px-6 py-3">
                <div className="flex items-center justify-center gap-2 text-xs text-gray-500">
                  <Lock className="h-3 w-3" />
                  <span>Unauthorized reproduction or distribution is prohibited.</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
};

export default MaterialViewerPage;
